import { SaveArtefact } from '@modules/common/components/SaveArtefact';
import {
  useCopyToClipboard,
  useIsSearchToFeedEnabled,
} from '@modules/common/hooks';
import { ArtefactType, SaveArtefactFunction } from '@modules/common/types';
import { getArtefactTypeLabel } from '@modules/common/utils';
import LinkIcon from '@mui/icons-material/Link';
import { Dialog, IconButton } from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { useState } from 'react';
import { useIsAuthorized } from '../CanAccess/hooks';

interface CopyArtefactLinkButtonProps {
  artefactType: ArtefactType;
  artefactLink: string;
  onClick: (link: string) => void;
  onSave: SaveArtefactFunction;
  isSavedArtefact: boolean;
  isDisabled?: boolean;
  className?: string;
}

export const CopyArtefactLinkButton = ({
  artefactType,
  artefactLink,
  onClick,
  onSave,
  isSavedArtefact,
  isDisabled = false,
  className,
}: CopyArtefactLinkButtonProps) => {
  const canSaveArtefacts = useIsAuthorized({
    resource: PermissionResource.Artefact,
    operation: PermissionOperation.Write,
  });

  const isSearchToFeedEnabled = useIsSearchToFeedEnabled();

  const [isSaveArtefactFormOpen, setIsSaveArtefactFormOpen] = useState(false);

  const copyTextToClipboard = useCopyToClipboard();

  const artefactTypeLabel = getArtefactTypeLabel(
    artefactType,
    isSearchToFeedEnabled,
  );

  const onCopy = () => {
    copyTextToClipboard(
      artefactLink,
      `${artefactTypeLabel} link copied to clipboard!`,
    );
    onClick(artefactLink);
  };

  const onCopyClick = () => {
    if (isSavedArtefact) {
      onCopy();
      return;
    }

    setIsSaveArtefactFormOpen(true);
  };

  if (!canSaveArtefacts && !isSavedArtefact) {
    return null;
  }

  return (
    <>
      <IconButton
        color="primary"
        onClick={onCopyClick}
        disabled={isDisabled}
        className={className}
      >
        <LinkIcon />
      </IconButton>
      <Dialog
        fullWidth
        maxWidth="sm"
        PaperProps={{ elevation: 3 }}
        open={isSaveArtefactFormOpen}
        onClose={() => setIsSaveArtefactFormOpen(false)}
      >
        <SaveArtefact
          onSubmit={onSave}
          onClose={() => setIsSaveArtefactFormOpen(false)}
          artefactType={artefactType}
          onCopyLink={onClick}
        />
      </Dialog>
    </>
  );
};
